import {useEffect, useMemo, useRef, useState} from 'react';

import * as ExpectedPanelsActions from '../../../state/expectedPanels/actions';
import {useReduxAction, useReduxStore} from '../../../state/hooks';
import {useRunsWandbConfigQuery} from '../../../state/runs/hooks';
import * as ViewHooks from '../../../state/views/hooks';
import {denormalize} from '../../../state/views/normalize';
import * as PanelBankConfigActionsInternal from '../../../state/views/panelBankConfig/actionsInternal';
import * as PanelBankConfigTypes from '../../../state/views/panelBankConfig/types';
import {WholeFromTypeWithRef} from '../../../state/views/types';
import {getCache, getCacheStorageKey} from '../../../util/cache';
import {getExpectedPanelSpecs} from '../../../util/panelbank';
import {useKeyInfoQueryContext} from '../../MultiRunWorkspace/KeyInfoQueryContext';
import {usePanelsSharedRefsContext} from '../../Views/contexts/PanelsSharedRefsContext';
import {useWorkspaceRefsContext} from '../../Workspace/WorkspaceRefsContext';
import {useWorkspaceSettingsContext} from '../../WorkspaceDrawer/Settings/WorkspaceSettingsContextProvider';

export const useAutoSyncPanelBank = (
  panelBankConfigRef: PanelBankConfigTypes.Ref
): {loading: boolean} => {
  const updateExpectedPanels = useReduxAction(ExpectedPanelsActions.update);
  const clearExpectedPanels = useReduxAction(ExpectedPanelsActions.clear);
  const diffAndInitPanels = ViewHooks.useViewAction(
    panelBankConfigRef,
    PanelBankConfigActionsInternal.diffAndInitPanels
  );

  // keep expected panels up to date
  const {keyInfoQuery} = useKeyInfoQueryContext();
  const {runSetRefs} = usePanelsSharedRefsContext();
  const wbConfigQuery = useRunsWandbConfigQuery(runSetRefs);
  const {
    workspaceLayout: {autoOrganizePrefix, shouldAutoGeneratePanels},
  } = useWorkspaceSettingsContext();
  const {workspaceSettingsRef} = useWorkspaceRefsContext();

  const replacePanelBankConfig = useReduxAction(
    PanelBankConfigActionsInternal.replacePanelBankConfig
  );

  const [loading, setLoading] = useState<boolean>(true);

  const cache = getCache();

  const latestCacheKey = useRef<string | null>(null);

  const store = useReduxStore();

  const isFirstPBRender = useRef(true);

  useEffect(() => {
    if (
      keyInfoQuery.loading ||
      keyInfoQuery.error ||
      wbConfigQuery.loading ||
      wbConfigQuery.error ||
      !wbConfigQuery.wandbConfig
    ) {
      return;
    }

    // We need this to not have any refs in the cache key
    // so that our cache key can be stable. Thats why we
    // use denormalize here. We also want to be very conservative
    // about calling denormalize because it's expensive. So we only
    // want to run it if the history keys or config have changed.
    const getCurrentPanelBankConfig = () => {
      const panelBankConfig = denormalize(
        store.getState().views.parts,
        panelBankConfigRef,
        {
          includeRefs: false,
        }
      );
      return panelBankConfig;
    };

    // must include `sortAlphabetically` workspace setting in cache key,
    // but we don't want to trigger panel gen whenever alpha sort setting
    // is touched. therefore, we have to get the setting value directly
    // from the store *inside* this effect hook
    const {sortAlphabetically} =
      store.getState().views.parts[workspaceSettingsRef.type][
        workspaceSettingsRef.id
      ] ?? {};

    const cacheKey = {
      keys: keyInfoQuery.historyKeyInfo.sortedKeys,
      viz: keyInfoQuery.viz,
      panelBankConfig: getCurrentPanelBankConfig(),
      config: wbConfigQuery.wandbConfig,
      autoOrganizePrefix,
      shouldAutoGeneratePanels,
      sortAlphabetically,
    };

    const doDiffAndInitPanels = () => {
      const expectedPanels = getExpectedPanelSpecs(
        keyInfoQuery.historyKeyInfo.keys,
        keyInfoQuery.viz,
        autoOrganizePrefix,
        wbConfigQuery.wandbConfig
      );
      updateExpectedPanels(expectedPanels);
      diffAndInitPanels(expectedPanels, workspaceSettingsRef);
      setLoading(false);
    };

    if (cache && isFirstPBRender.current) {
      const fileName = getCacheStorageKey('autoSyncPanelBank', cacheKey);
      latestCacheKey.current = fileName;

      (async () => {
        if (fileName) {
          const cachedData = await cache.get<
            WholeFromTypeWithRef<'panel-bank-config'>
          >(fileName);

          if (fileName !== latestCacheKey.current) {
            // stale request, ignore
            return;
          }

          if (cachedData) {
            replacePanelBankConfig(cachedData.data, panelBankConfigRef);
            setLoading(false);
          } else {
            doDiffAndInitPanels();
            const panelBankConfig = getCurrentPanelBankConfig();
            cache.set<WholeFromTypeWithRef<'panel-bank-config'>>(
              fileName,
              panelBankConfig
            );
          }
        } else {
          doDiffAndInitPanels();
        }
      })();
    } else {
      doDiffAndInitPanels();
    }

    isFirstPBRender.current = false;

    return () => {
      clearExpectedPanels();
    };
  }, [
    keyInfoQuery,
    wbConfigQuery,
    autoOrganizePrefix,
    updateExpectedPanels,
    clearExpectedPanels,
    diffAndInitPanels,
    workspaceSettingsRef,
    setLoading,
    cache,
    panelBankConfigRef,
    replacePanelBankConfig,
    shouldAutoGeneratePanels,
    store,
  ]);
  return useMemo(() => ({loading}), [loading]);
};
